'use client';

import { LockClosedIcon, UserIcon } from '@heroicons/react/24/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import type { XiorError } from 'xior';
import { z } from 'zod';
import { signInAction } from '@/actions/sign-in.action';
import { usePostApiPlayers } from '@/api/backoffice/players/players';
import type { CreatePlayerCommand } from '@/api/backoffice-schemas';
import { Button } from '@/components/button';
import { Input } from '@/components/Input/Input';
import { PlayerAvatarIcon } from '@/components/icons/PlayerAvatarIcon';

// Zod validation schema
const registerSchema = z
  .object({
    email: z
      .string()
      .min(1, 'Email is required')
      .email('Invalid email format')
      .max(50, 'Email is too long'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .min(1, 'Password is required')
      .max(50, 'Password is too long'),
    passwordAgain: z
      .string()
      .min(1, 'Confirm Password is required')
      .max(50, 'Password is too long'),
    username: z
      .string()
      .min(1, 'Username is required')
      .max(50, 'Username is too long'),
  })
  .refine((data) => data.password === data.passwordAgain, {
    message: 'Passwords must match',
    path: ['passwordAgain'],
  });

export const SignUpForm = () => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Omit<CreatePlayerCommand, 'url'>>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: '',
      password: '',
      passwordAgain: '',
      username: '',
    },
  });

  const { mutateAsync: signUpAction } = usePostApiPlayers();

  const onSubmit = async (data: Omit<CreatePlayerCommand, 'url'>) => {
    try {
      await signUpAction({
        data,
      });
      await signInAction(data);
      setShowConfirmation(true);
      await queryClient.invalidateQueries();
    } catch (error) {
      const err = error as XiorError;
      toast.error(err.response?.data.errors?.[0] ?? err.response?.data.message);
    }
  };

  if (showConfirmation) {
    return (
      <div className="flex max-w-[500px] flex-col items-center justify-center p-6 text-center">
        <h2 className="mb-2 font-semibold text-2xl">
          Thank you for registering!
        </h2>
        <p className="mb-4 text-base text-light-blue">
          We&apos;ve sent a confirmation link to your email address.
        </p>
        <p className="text-sm">
          Please check your email inbox and click on the confirmation link to
          verify your email.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-4 flex w-full max-w-[480px] flex-col gap-3 self-center">
        <h2 className="text-balance font-semibold text-2xl">
          Create an GOCORE account
        </h2>
        <p className="hidden font-normal text-base leading-6 md:block">
          Showoff your knowledge in the GOCORE community. Climb through the
          leader boards and become the best tipster.
        </p>
      </div>

      <div className="flex w-full max-w-[480px] flex-col gap-5 self-center">
        <div className="flex w-full flex-col gap-2">
          <Input
            {...register('email')}
            error={errors.email?.message}
            label="Email"
            leftIcon={<UserIcon className="size-5" />}
            placeholder="Your email"
            type="text"
          />

          <Input
            {...register('password')}
            error={errors.password?.message}
            label="Password"
            leftIcon={<LockClosedIcon className="size-5" />}
            placeholder="Your password"
            type="password"
          />

          <Input
            {...register('passwordAgain')}
            error={errors.passwordAgain?.message}
            label="Password again"
            leftIcon={<LockClosedIcon className="size-5" />}
            placeholder="Confirm Password"
            type="password"
          />
        </div>

        <div>
          <Input
            {...register('username')}
            error={errors.username?.message}
            label="Username"
            leftIcon={
              <PlayerAvatarIcon
                className="size-5"
                desc="Player avatar icon for username"
                title="Username"
              />
            }
            placeholder="Username"
            type="text"
            wrapperClassName="mb-1"
          />

          <div className="text-sm">
            Used to display on tips, pickems and leaderboards
          </div>
        </div>

        <Button
          aria-busy={isSubmitting}
          disabled={isSubmitting}
          onClick={(e) => e.stopPropagation()}
          size="lg"
          type="submit"
          variant="primary"
        >
          {isSubmitting ? 'Registering...' : 'Register'}
        </Button>
      </div>
    </form>
  );
};
